import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Currency } from '@freelancer/datastore/collections';
import { ButtonColor } from '@freelancer/ui/button';
import { IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType } from '@freelancer/ui/text';
import { TooltipPosition } from '@freelancer/ui/tooltip';
import { HireMeUser } from 'app/hire-me/hire-me.types';

@Component({
  selector: 'app-hire-me-hourly-confirmation-form',
  template: `
    <app-hourly-award-form-layout [withSeparator]="true">
      <fl-bit HourlyAwardForm="body">
        <app-hourly-award-heading
          title="Set up your Hourly Project"
          i18n-title="Hire me hourly confirmation form title"
          subtitle="Review your billing details to award this project."
          i18n-subtitle="Hire me hourly confirmation form description"
        ></app-hourly-award-heading>

        <fl-list>
          <fl-list-item>
            <fl-bit class="HireMeHourlyForm-item">
              <fl-bit class="HireMeHourlyForm-item-field">
                <fl-text
                  i18n="Hire me weekly limit label"
                  [flMarginRight]="Margin.XXSMALL"
                  [fontType]="FontType.STRONG"
                >
                  Weekly Limit
                </fl-text>
                <fl-tooltip
                  i18n-message="Hire me weekly limit explainer"
                  message="
                    This is the maximum number of hours your freelancer will be able to track each week.
                  "
                  [position]="TooltipPosition.BOTTOM_START"
                >
                  <fl-icon
                    [name]="'ui-info-v2'"
                    [size]="IconSize.SMALL"
                  ></fl-icon>
                </fl-tooltip>
              </fl-bit>

              <fl-text i18n="Hire me hourly weekly limit">
                {{ weeklyLimit }} hours/week
              </fl-text>
            </fl-bit>
          </fl-list-item>

          <fl-list-item>
            <fl-bit class="HireMeHourlyForm-item">
              <fl-bit class="HireMeHourlyForm-item-field">
                <fl-text
                  i18n="Hire me hourly rate label"
                  [flMarginRight]="Margin.XXSMALL"
                  [fontType]="FontType.STRONG"
                >
                  Hourly Rate
                </fl-text>
                <fl-tooltip
                  i18n-message="Hire me hourly rate explainer"
                  message="
                    The amount you will be billed for every hour your freelancer tracks on your project.
                  "
                  [position]="TooltipPosition.BOTTOM_START"
                >
                  <fl-icon
                    [name]="'ui-info-v2'"
                    [size]="IconSize.SMALL"
                  ></fl-icon>
                </fl-tooltip>
              </fl-bit>

              <fl-text>
                {{ hourlyRate | flCurrency: currency.code }}
              </fl-text>
            </fl-bit>
          </fl-list-item>

          <fl-list-item>
            <fl-bit class="HireMeHourlyForm-item">
              <fl-bit class="HireMeHourlyForm-item-field">
                <fl-text
                  i18n="Hire me maximum weekly bill amount label"
                  [flMarginRight]="Margin.XXSMALL"
                  [fontType]="FontType.STRONG"
                >
                  Maximum Weekly Bill
                </fl-text>
                <fl-tooltip
                  i18n-message="Hire me award maximum weekly bill explainer"
                  message="
                    The maximum amount you will be charged each week, if your freelancer tracks up to the weekly limit.
                  "
                  [position]="TooltipPosition.BOTTOM_START"
                >
                  <fl-icon
                    [name]="'ui-info-v2'"
                    [size]="IconSize.SMALL"
                  ></fl-icon>
                </fl-tooltip>
              </fl-bit>

              <fl-text>
                {{ maxWeeklyBill | flCurrency: currency.code }}
              </fl-text>
            </fl-bit>
          </fl-list-item>

          <fl-hr></fl-hr>
        </fl-list>
      </fl-bit>

      <fl-bit HourlyAwardForm="cta">
        <fl-text
          *ngIf="paymentVerifyRequired"
          i18n="Hire Me hourly confirmation payment method notice"
          [flMarginBottom]="Margin.XSMALL"
          [color]="FontColor.MID"
        >
          You will NOT be charged right now, a payment method needs to be set up
          so that you are prepared to pay your freelancer when they deliver
          their work.
        </fl-text>
        <app-hourly-award-cta-layout>
          <fl-bit HourlyAwardCTA="awardButton">
            <fl-button
              *ngIf="!paymentVerifyRequired; else paymentVerifyButton"
              i18n="Award button"
              flTrackingLabel="AwardBidButton"
              [color]="ButtonColor.SUCCESS"
              (click)="confirm.emit(true)"
            >
              Award
            </fl-button>
            <ng-template #paymentVerifyButton>
              <fl-button
                i18n="Payment verify and award button"
                flTrackingLabel="PaymentVerifyButton"
                [color]="ButtonColor.SUCCESS"
                (click)="confirm.emit(true)"
              >
                Add Payment Method and Award
              </fl-button>
            </ng-template>
          </fl-bit>
        </app-hourly-award-cta-layout>
      </fl-bit>
    </app-hourly-award-form-layout>
  `,
  styleUrls: ['./hire-me-hourly-confirmation-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HireMeHourlyConfirmationFormComponent implements OnInit {
  Margin = Margin;
  FontType = FontType;
  TooltipPosition = TooltipPosition;
  IconSize = IconSize;
  FontColor = FontColor;
  ButtonColor = ButtonColor;

  @Input() hireMeUser: HireMeUser;
  @Input() currency: Currency;
  @Input() weeklyLimit: number;
  @Input() hourlyRate: number;
  @Input() maxWeeklyBill: number;
  @Input() paymentVerifyWhiteListed: boolean;
  @Output() confirm = new EventEmitter<boolean>();

  paymentVerifyRequired: boolean;

  ngOnInit() {
    this.paymentVerifyRequired =
      !this.paymentVerifyWhiteListed &&
      this.hireMeUser &&
      !this.hireMeUser.paymentVerified;
  }
}
